import { useState, useMemo, memo } from 'react';
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal, Repeat2, Pencil, Trash2, Archive, Flag, UserPlus, UserMinus, User, Megaphone, Trophy, Briefcase, Users, Timer, ClipboardList, Search, BadgeCheck, Ban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UserAvatar from '@/components/UserAvatar';
import MediaCarousel from '@/components/feed/MediaCarousel';
import type { MediaItem } from '@/components/feed/MediaCarousel';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import CommentModal from './CommentModal';
import OfferPreviewModal from './OfferPreviewModal';
import ShareSheet from './ShareSheet';
import { cn } from '@/lib/utils';
import type { FeedPost, PostType } from '@/store/feed';
import { getUserById } from '@/store/users';
import { toast } from 'sonner';
import { getOfferCountdown } from '@/utils/offerCountdown';
import { usePreferences } from '@/store/preferences';
import { useAppStoreOptional } from '@/store/AppStoreProvider';
import { translateCategory } from '@/i18n/translations';

interface PostCardProps {
  post: FeedPost;
  liked: boolean;
  saved: boolean;
  currentUserId: string;
  isFollowingAuthor: boolean;
  currentUserRole?: string;
  hasApplied?: boolean;
  applicantCount?: number;
  offer?: import('@/store/offers').Offer;
  offerCreatorName?: string;
  onLike: () => void;
  onComment: (text: string, parentCommentId?: string, mentions?: import('@/store/feed').CommentMention[]) => void;
  onSave: () => void;
  onRepost: () => void;
  onShare: () => void;
  onEditCaption?: (newContent: string, taggedUserIds?: string[]) => void;
  onDelete?: () => void;
  onArchive?: () => void;
  onReport?: (reason: string) => void;
  onToggleFollowAuthor?: () => void;
  onApply?: () => void;
  onContactCreator?: () => void;
  onSendInApp?: () => void;
  onDeleteComment?: (commentId: string) => void;
  onToggleCommentLike?: (commentId: string) => void;
  onPinComment?: (commentId: string | undefined) => void;
  onReportComment?: (commentId: string, reason: import('@/store/feed').CommentReportReason) => void;
  onHideComment?: (commentId: string) => void;
  canPostComment?: (text: string) => { allowed: boolean; message?: string };
  onRecordCommentTime?: (text: string) => void;
}

const TYPE_CONFIG: Record<PostType, { labelKey: 'role_player' | 'role_club' | 'role_coach' | 'role_recruiter' | 'role_media' | 'offer'; icon: React.ReactNode; badgeClass: string }> = {
  player: { labelKey: 'role_player', icon: <Trophy className="h-3 w-3" />, badgeClass: 'bg-primary/15 text-primary border-primary/25' },
  club: { labelKey: 'role_club', icon: <Megaphone className="h-3 w-3" />, badgeClass: 'bg-blue-500/15 text-blue-400 border-blue-500/25' },
  offer: { labelKey: 'offer', icon: <Briefcase className="h-3 w-3" />, badgeClass: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' },
  media: { labelKey: 'role_media', icon: <Megaphone className="h-3 w-3" />, badgeClass: 'bg-violet-500/15 text-violet-400 border-violet-500/25' },
  coach: { labelKey: 'role_coach', icon: <ClipboardList className="h-3 w-3" />, badgeClass: 'bg-amber-500/15 text-amber-400 border-amber-500/25' },
  recruiter: { labelKey: 'role_recruiter', icon: <Search className="h-3 w-3" />, badgeClass: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25' },
};

const PostCard = ({
  post, liked, saved, currentUserId, isFollowingAuthor, currentUserRole, hasApplied, applicantCount,
  offer: linkedOffer, offerCreatorName,
  onLike, onComment, onSave, onRepost, onShare,
  onEditCaption, onDelete, onArchive, onReport, onToggleFollowAuthor, onApply, onContactCreator, onSendInApp, onDeleteComment, onToggleCommentLike, onPinComment,
  onReportComment, onHideComment, canPostComment, onRecordCommentTime,
}: PostCardProps) => {
  const appStore = useAppStoreOptional();
  const [commentOpen, setCommentOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [offerPreviewOpen, setOfferPreviewOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [editText, setEditText] = useState(post.content);
  const [reportReason, setReportReason] = useState('');
  const [likePopKey, setLikePopKey] = useState(0);
  const navigate = useNavigate();
  const { t, language } = usePreferences();
  const typeLabel = (key: string): string => key === 'offer' ? t.offer : ((t as any)[key] ?? key);

  const handleDoubleTapLike = () => {
    if (!liked) onLike();
    setLikePopKey((k) => k + 1);
  };

  const displayedAuthorId = post.originalPostId ? (post.originalAuthorId ?? post.authorId) : post.authorId;
  const authorUser = getUserById(displayedAuthorId);
  const isAuthor = displayedAuthorId === currentUserId;
  // Always reflect the real author's role in the badge.
  const roleToType: Record<string, PostType> = {
    player: 'player', club: 'club', media: 'media', coach: 'coach', recruiter: 'recruiter',
  };
  const badgeType: PostType = post.postType === 'offer'
    ? 'offer'
    : (authorUser?.role && roleToType[authorUser.role]) || post.postType || 'player';
  const typeConfig = TYPE_CONFIG[badgeType];

  const handleShare = () => setShareSheetOpen(true);
  const handleCopyLink = () => { onShare(); toast('Link copied to clipboard'); };
  const handleShareExternal = () => {
    if (navigator.share) {
      navigator.share({ title: `Post by ${post.authorName}`, text: post.content, url: window.location.href }).catch(() => {});
    } else {
      onShare(); toast('Link copied to clipboard');
    }
  };
  const handleRepost = () => {
    if (post.repostedBy.length > 0 && post.originalPostId) return;
    onRepost(); toast('Reposted to your feed');
  };
  const handleEditSave = () => {
    if (editText.trim() && onEditCaption) { onEditCaption(editText.trim()); toast.success('Caption updated'); }
    setEditDialogOpen(false);
  };
  const handleReportSubmit = () => {
    if (reportReason.trim() && onReport) { onReport(reportReason.trim()); toast.success('Post reported'); }
    setReportReason(''); setReportDialogOpen(false);
  };

  const goToAuthor = () => navigate(`/profile/${displayedAuthorId}`);
  const goToReposter = () => navigate(`/profile/${post.authorId}`);

  const mediaItems: MediaItem[] = post.mediaItems?.length
    ? post.mediaItems
    : post.imageUrl
      ? [{ url: post.imageUrl, type: 'image' as const }]
      : [];
  const hasMedia = mediaItems.length > 0;
  const isTextOnly = !hasMedia && post.postType !== 'offer' && !(post.offerId && linkedOffer);

  const relativeTime = (iso?: string) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return t.now;
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return `${Math.floor(d / 7)}w`;
  };

  const moreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="text-muted-foreground hover:text-foreground">
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-card border-border">
        {isAuthor ? (
          <>
            <DropdownMenuItem onClick={() => { setEditText(post.content); setEditDialogOpen(true); }}>
              <Pencil className="mr-2 h-4 w-4" /> {t.edit_caption}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onArchive?.()}>
              <Archive className="mr-2 h-4 w-4" /> {t.archive_post}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} className="text-destructive focus:text-destructive">
              <Trash2 className="mr-2 h-4 w-4" /> {t.delete_post}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={() => navigate(`/profile/${displayedAuthorId}`)}>
              <User className="mr-2 h-4 w-4" /> {t.view_profile_action}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onToggleFollowAuthor?.()}>
              {isFollowingAuthor ? <><UserMinus className="mr-2 h-4 w-4" /> {t.unfollow}</> : <><UserPlus className="mr-2 h-4 w-4" /> {t.follow}</>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSave()}>
              <Bookmark className="mr-2 h-4 w-4" /> {saved ? t.unsave_post : t.save_post}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setReportDialogOpen(true)} className="text-destructive focus:text-destructive">
              <Flag className="mr-2 h-4 w-4" /> {t.report_post}
            </DropdownMenuItem>
            {appStore && displayedAuthorId && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => {
                  void appStore.block.blockUser(displayedAuthorId).then(({ error }) => {
                    toast[error ? 'error' : 'success'](error ? 'Block failed' : 'User blocked');
                  });
                }}
              >
                <Ban className="mr-2 h-4 w-4" /> {language === 'fr' ? 'Bloquer cet utilisateur' : 'Block this user'}
              </DropdownMenuItem>
            )}

          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const modals = (
    <>
      <ShareSheet
        open={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        onCopyLink={handleCopyLink}
        onShareExternal={handleShareExternal}
        onSendInApp={onSendInApp}
      />

      <CommentModal
        open={commentOpen}
        onClose={() => setCommentOpen(false)}
        comments={post.comments}
        onSubmit={(text, parentCommentId, mentions) => onComment(text, parentCommentId, mentions)}
        onDeleteComment={onDeleteComment}
        onToggleCommentLike={onToggleCommentLike}
        onPinComment={onPinComment}
        onReportComment={onReportComment}
        onHideComment={onHideComment}
        pinnedCommentId={post.pinnedCommentId}
        postAuthorName={post.authorName}
        postAuthorId={displayedAuthorId}
        currentUserId={currentUserId}
        canPostComment={canPostComment}
        onRecordCommentTime={onRecordCommentTime}
      />

      {linkedOffer && (
        <OfferPreviewModal
          open={offerPreviewOpen}
          onClose={() => setOfferPreviewOpen(false)}
          offer={linkedOffer}
          creatorName={offerCreatorName ?? 'Unknown'}
          applicantCount={applicantCount ?? 0}
          hasApplied={hasApplied ?? false}
          canApply={currentUserRole === 'player' && !!onApply}
          onApply={() => {
            onApply?.();
            toast.success(t.application_submitted);
          }}
          onContactCreator={linkedOffer.createdById !== currentUserId ? onContactCreator : undefined}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">{t.delete_post_q}</AlertDialogTitle>
            <AlertDialogDescription>{t.delete_post_desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">{t.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onDelete?.(); toast.success(t.post_deleted); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t.delete}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground text-lg font-bold">{t.edit_post}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t.caption}</p>
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="min-h-[120px] rounded-xl bg-secondary border-border text-foreground placeholder:text-muted-foreground resize-none focus:ring-primary/30"
                maxLength={500}
                placeholder={t.write_caption}
              />
              <p className="text-right text-[10px] text-muted-foreground">{editText.length}/500</p>
            </div>
            {post.taggedUserIds && post.taggedUserIds.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t.tagged_people}</p>
                <div className="flex flex-wrap gap-1.5">
                  {post.taggedUserIds.map((id) => {
                    const u = getUserById(id);
                    return u ? (
                      <span key={id} className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                        @{u.username}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
            {post.mediaItems && post.mediaItems.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {post.mediaItems.length} {t.media}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-xl border-border">{t.cancel}</Button>
            <Button onClick={handleEditSave} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90" disabled={!editText.trim()}>{t.save_changes}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle className="text-foreground">{t.report_post}</DialogTitle></DialogHeader>
          <Textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder={t.report_reason_placeholder} className="min-h-[100px] bg-secondary border-border text-foreground" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleReportSubmit} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t.report}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (isTextOnly) {
    return (
      <>
        <article className="px-4 py-4 border-b border-border/40 transition-all duration-200">
          {post.originalPostId && (
            <div className="flex items-center gap-1.5 pb-2 pl-12 text-xs text-muted-foreground">
              <Repeat2 className="h-3.5 w-3.5" />
              <span>
                <button onClick={goToReposter} className="hover:underline">{post.authorName}</button>
                {' '}{t.reposted_label}
              </span>
            </div>
          )}
          <div className="flex items-start gap-3">
            <button onClick={goToAuthor} className="shrink-0 self-start">
              <UserAvatar
                avatarUrl={authorUser?.avatarUrl}
                initials={post.originalPostId ? post.originalAuthorName?.charAt(0) ?? 'R' : post.authorInitials}
                className="h-10 w-10 ring-1 ring-border"
                fallbackClassName="text-xs"
              />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <button onClick={goToAuthor} className="text-[15px] font-bold text-foreground hover:underline truncate">
                    {post.originalPostId ? post.originalAuthorName : post.authorName}
                  </button>
                  {authorUser?.verified && (
                    <BadgeCheck className="h-3.5 w-3.5 text-primary fill-primary/20 shrink-0" />
                  )}
                  <span className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider', typeConfig.badgeClass)}>
                    {typeConfig.icon}
                    {typeLabel(typeConfig.labelKey)}
                  </span>
                  {authorUser?.username && (
                    <span className="text-[13px] text-muted-foreground truncate">@{authorUser.username}</span>
                  )}
                  {post.createdAt && (
                    <>
                      <span className="text-[13px] text-muted-foreground">·</span>
                      <span className="text-[13px] text-muted-foreground">{relativeTime(post.createdAt)}</span>
                    </>
                  )}
                </div>
                {moreMenu}
              </div>

              <p className="mt-1 text-[15px] leading-[1.45] text-foreground whitespace-pre-wrap break-words">
                {post.content}
              </p>

              {post.taggedUserIds && post.taggedUserIds.length > 0 && (() => {
                const tagged = post.taggedUserIds.map((id) => getUserById(id)).filter(Boolean);
                if (tagged.length === 0) return null;
                return (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    <span>{t.with_label} </span>
                    {tagged.map((u, i) => u && (
                      <span key={u.id}>
                        {i > 0 && (i === tagged.length - 1 ? ` ${t.with_label === 'With' ? 'and' : 'et'} ` : ', ')}
                        <button onClick={() => navigate(`/profile/${u.id}`)} className="font-semibold text-primary hover:underline">
                          @{u.username}
                        </button>
                      </span>
                    ))}
                  </p>
                );
              })()}

              {/* Compact action row — X-style */}
              <div className="mt-3 flex items-center justify-between max-w-md text-muted-foreground">
                <button onClick={() => setCommentOpen(true)} className="group flex items-center gap-1.5 active:scale-95 transition-all duration-150 hover:text-foreground">
                  <MessageCircle className="h-[18px] w-[18px]" />
                  {post.comments.length > 0 && (
                    <span className="text-xs">{post.comments.length}</span>
                  )}
                </button>
                <button onClick={handleRepost} className="group flex items-center gap-1.5 active:scale-95 transition-all duration-150 hover:text-foreground">
                  <Repeat2 className="h-[18px] w-[18px]" />
                </button>
                <button onClick={() => { if (!liked) setLikePopKey((k) => k + 1); onLike(); }} className="group flex items-center gap-1.5 active:scale-95 transition-all duration-150 hover:text-foreground">
                  <Heart key={likePopKey} className={cn('h-[18px] w-[18px] transition-colors', likePopKey > 0 && 'animate-like-pop', liked ? 'fill-red-500 text-red-500' : '')} />
                  {post.likes.length > 0 && (
                    <span className={cn('text-xs', liked && 'text-red-500')}>{post.likes.length}</span>
                  )}
                </button>
                <div className="flex items-center gap-3">
                  <button onClick={onSave} className="active:scale-95 transition-all duration-150 hover:text-foreground">
                    <Bookmark className={cn('h-[18px] w-[18px]', saved && 'fill-foreground text-foreground')} />
                  </button>
                  <button onClick={handleShare} className="active:scale-95 transition-all duration-150 hover:text-foreground">
                    <Send className="h-[18px] w-[18px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {modals}
      </>
    );
  }

  return (
    <>
      <article className="px-4 py-4 border-b border-border/40 transition-all duration-200">

        {/* Repost banner */}
        {post.originalPostId && (
          <div className="flex items-center gap-1.5 pb-2 pl-[52px] text-xs text-muted-foreground">
            <Repeat2 className="h-3.5 w-3.5" />
            <span>
              <button onClick={goToReposter} className="hover:underline">{post.authorName}</button>
              {' '}{t.reposted_label}
            </span>
          </div>
        )}

        <div className="flex items-start gap-3">
          {/* Avatar — fixed top-left */}
          <button onClick={goToAuthor} className="shrink-0 self-start">
            <UserAvatar
              avatarUrl={authorUser?.avatarUrl}
              initials={post.originalPostId ? post.originalAuthorName?.charAt(0) ?? 'R' : post.authorInitials}
              className="h-10 w-10 ring-1 ring-border"
              fallbackClassName="text-xs"
            />
          </button>

          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={goToAuthor} className="text-[15px] font-bold text-foreground hover:underline truncate leading-tight">
                    {post.originalPostId ? post.originalAuthorName : post.authorName}
                  </button>
                  {authorUser?.verified && (
                    <BadgeCheck className="h-3.5 w-3.5 text-primary fill-primary/20 shrink-0" />
                  )}
                  <span className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider', typeConfig.badgeClass)}>
                    {typeConfig.icon}
                    {typeLabel(typeConfig.labelKey)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground leading-tight mt-0.5">
                  {authorUser?.username && <span className="truncate">@{authorUser.username}</span>}
                  {post.createdAt && <><span>·</span><span>{relativeTime(post.createdAt)}</span></>}
                  {post.category && <><span>·</span><span className="truncate">{translateCategory(post.category, t)}</span></>}
                </div>
              </div>
              {moreMenu}
            </div>

            {/* Caption */}
            {post.content && (
              <p className="mt-2 text-[15px] leading-[1.45] text-foreground whitespace-pre-wrap break-words">
                {post.content}
              </p>
            )}

            {/* Tagged users */}
            {post.taggedUserIds && post.taggedUserIds.length > 0 && (() => {
              const tagged = post.taggedUserIds.map((id) => getUserById(id)).filter(Boolean);
              if (tagged.length === 0) return null;
              return (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  <span>{t.with_label} </span>
                  {tagged.map((u, i) => u && (
                    <span key={u.id}>
                      {i > 0 && (i === tagged.length - 1 ? ` ${t.with_label === 'With' ? 'and' : 'et'} ` : ', ')}
                      <button onClick={() => navigate(`/profile/${u.id}`)} className="font-semibold text-primary hover:underline">
                        @{u.username}
                      </button>
                    </span>
                  ))}
                </p>
              );
            })()}

            {/* Media — rounded, below caption */}
            {hasMedia && (
              <div className="mt-3 overflow-hidden rounded-2xl border border-border/60">
                <MediaCarousel items={mediaItems} onDoubleTapLike={handleDoubleTapLike} />
              </div>
            )}

            {/* Offer metadata block — shown whenever an offer is attached and still exists */}
            {post.offerId && linkedOffer && (() => {
              const countdown = getOfferCountdown(linkedOffer.date);
              return (
                <div className="mt-3 space-y-3 rounded-2xl border border-border/60 bg-secondary/40 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-400">
                          <Briefcase className="h-3 w-3" />
                          {linkedOffer.type}
                        </span>
                        <span className={cn(
                          'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider',
                          countdown.expired
                            ? 'border-border bg-muted text-muted-foreground'
                            : countdown.urgent
                              ? 'border-destructive/30 bg-destructive/10 text-destructive'
                              : 'border-primary/30 bg-primary/10 text-primary'
                        )}>
                          <Timer className="h-3 w-3" />
                          {countdown.label}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm font-bold text-foreground truncate">{linkedOffer.title}</p>
                      {linkedOffer.description && (
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{linkedOffer.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {applicantCount !== undefined && (
                        <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{applicantCount} {applicantCount === 1 ? t.person : t.people}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setOfferPreviewOpen(true)}
                      className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                    >
                      {t.view_profile_action === 'View profile' ? 'View Offer' : 'Voir l\'offre'} →
                    </button>
                  </div>

                  {currentUserRole === 'player' && onApply && (
                    <Button
                      onClick={() => {
                        if (!hasApplied && !countdown.expired) {
                          onApply();
                          toast.success(t.application_submitted);
                        }
                      }}
                      disabled={hasApplied || countdown.expired}
                      className={cn(
                        'w-full rounded-full font-semibold transition-all duration-200 active:scale-[0.97]',
                        countdown.expired
                          ? 'bg-muted text-muted-foreground cursor-default'
                          : hasApplied
                            ? 'bg-primary/20 text-primary cursor-default'
                            : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20'
                      )}
                    >
                      {countdown.expired ? t.closed : hasApplied ? t.applied : t.apply_now}
                    </Button>
                  )}
                </div>
              );
            })()}

            {/* Action bar — X-style: comment, repost, like, save, share */}
            <div className="mt-3 flex items-center justify-between max-w-md text-muted-foreground">
              <button onClick={() => setCommentOpen(true)} className="group flex items-center gap-1.5 active:scale-95 transition-all duration-150 hover:text-foreground">
                <MessageCircle className="h-[18px] w-[18px]" />
                {post.comments.length > 0 && (
                  <span className="text-xs">{post.comments.length}</span>
                )}
              </button>
              <button onClick={handleRepost} className="group flex items-center gap-1.5 active:scale-95 transition-all duration-150 hover:text-emerald-400">
                <Repeat2 className="h-[18px] w-[18px]" />
              </button>
              <button onClick={() => { if (!liked) setLikePopKey((k) => k + 1); onLike(); }} className="group flex items-center gap-1.5 active:scale-95 transition-all duration-150 hover:text-primary">
                <Heart key={likePopKey} className={cn('h-[18px] w-[18px] transition-colors', likePopKey > 0 && 'animate-like-pop', liked ? 'fill-primary text-primary' : '')} />
                {post.likes.length > 0 && (
                  <span className={cn('text-xs', liked && 'text-primary')}>{post.likes.length}</span>
                )}
              </button>
              <div className="flex items-center gap-3">
                <button onClick={onSave} className="active:scale-95 transition-all duration-150 hover:text-foreground">
                  <Bookmark className={cn('h-[18px] w-[18px]', saved && 'fill-foreground text-foreground')} />
                </button>
                <button onClick={handleShare} className="active:scale-95 transition-all duration-150 hover:text-foreground">
                  <Send className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>

            {/* Comment preview */}
            {(() => {
              const topLevel = post.comments.filter((c) => !c.parentCommentId);
              const totalCount = post.comments.length;
              if (totalCount === 0) return null;
              return (
                <div className="mt-2 space-y-1.5">
                  {topLevel.slice(-2).map((c) => {
                    const cUser = getUserById(c.authorId);
                    return (
                      <div key={c.id} className="flex items-start gap-2">
                        <UserAvatar avatarUrl={cUser?.avatarUrl} initials={c.authorInitials} className="h-5 w-5 shrink-0" fallbackClassName="text-[8px]" />
                        <p className="text-[13px] text-foreground leading-snug">
                          <span className="font-semibold text-foreground">{c.authorName}</span>{' '}{c.text}
                        </p>
                      </div>
                    );
                  })}
                  {totalCount > 2 && (
                    <button onClick={() => setCommentOpen(true)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                      {t.comments} ({totalCount})
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </article>

      {modals}
    </>
  );
};

export default memo(PostCard, (prev, next) => (
  prev.post === next.post &&
  prev.liked === next.liked &&
  prev.saved === next.saved &&
  prev.currentUserId === next.currentUserId &&
  prev.isFollowingAuthor === next.isFollowingAuthor &&
  prev.currentUserRole === next.currentUserRole &&
  prev.hasApplied === next.hasApplied &&
  prev.applicantCount === next.applicantCount &&
  prev.offer === next.offer &&
  prev.offerCreatorName === next.offerCreatorName
));
